import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faMicrophoneSlash,
  faVolumeUp,
  faVolumeMute,
  faCog,
} from "@fortawesome/free-solid-svg-icons";

interface VoiceControlsProps {
  isMuted: boolean;
  isEnabled: boolean;
  volume: number;
  onToggleMute: () => void;
  onToggleEnabled: () => void;
  onVolumeChange: (_volume: number) => void;
  onOpenSettings: () => void;
  participantVolumes?: Map<string, { name: string; volume: number; isMuted: boolean }>;
}

export const VoiceControls: React.FC<VoiceControlsProps> = ({
  isMuted,
  isEnabled,
  volume,
  onToggleMute,
  onToggleEnabled,
  onVolumeChange,
  onOpenSettings,
  participantVolumes = new Map(),
}) => {
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  return (
    <div className="voice-controls flex items-center gap-2 p-2 bg-gray-800 rounded-lg">
      {/* Enable/Disable Voice */}
      <button
        onClick={onToggleEnabled}
        className={`p-2 rounded transition-colors ${
          isEnabled
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-gray-600 hover:bg-gray-700 text-gray-300"
        }`}
        title={isEnabled ? "Disable Voice Chat" : "Enable Voice Chat"}
      >
        <FontAwesomeIcon icon={isEnabled ? faVolumeUp : faVolumeMute} />
      </button>

      {/* Mute/Unmute Microphone */}
      <button
        onClick={onToggleMute}
        disabled={!isEnabled}
        className={`p-2 rounded transition-colors ${
          !isEnabled
            ? "bg-gray-700 text-gray-500 cursor-not-allowed"
            : isMuted
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-blue-600 hover:bg-blue-700 text-white"
        }`}
        title={isMuted ? "Unmute Microphone" : "Mute Microphone"}
      >
        <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} />
      </button>

      {/* Volume Control */}
      <div
        className="relative"
        onMouseEnter={() => setShowVolumeSlider(true)}
        onMouseLeave={() => setShowVolumeSlider(false)}
      >
        <button
          className="p-2 rounded bg-gray-700 hover:bg-gray-600 text-white transition-colors"
          title="Volume Control"
        >
          <FontAwesomeIcon icon={volume > 0 ? faVolumeUp : faVolumeMute} />
        </button>

        {showVolumeSlider && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-800 rounded shadow-lg">
            <input
              type="range"
              min="0"
              max="100"
              value={volume * 100}
              onChange={(e) => onVolumeChange(parseInt(e.target.value) / 100)}
              className="w-24 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              style={{
                writingMode: "vertical-lr" as React.CSSProperties["writingMode"],
                WebkitAppearance: "slider-vertical",
                height: "100px",
              }}
            />
            <div className="text-xs text-center text-gray-300 mt-1">
              {Math.round(volume * 100)}%
            </div>
          </div>
        )}
      </div>

      {/* Settings Button */}
      <button
        onClick={onOpenSettings}
        className="p-2 rounded bg-gray-700 hover:bg-gray-600 text-white transition-colors"
        title="Audio Settings"
      >
        <FontAwesomeIcon icon={faCog} />
      </button>

      {/* Participant Count */}
      {participantVolumes.size > 0 && (
        <div className="ml-2 text-sm text-gray-300">
          {participantVolumes.size} participant{participantVolumes.size !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default VoiceControls;
